/*!
 * mapHandles.js — map:'s handles: the points dragged onto a surface in the room
 * © 2026 Rob Canning — GPLv3
 *
 * A thing with map: (a shape, a picture, a box) has a handle at each of its
 * points -- four corners, or a polygon's own corners with map:points -- and
 * one in the middle that moves them all. Dragging one bends the thing to
 * meet it (mapWarp.js); the cue that owns it does the bending, told by
 * onMove. The points are fractions of the stage (stageBand), so they mean
 * the same on the laptop and the projector.
 *
 * The handles show while mapping is on: running a line with map:1 (or
 * map:points) turns it on; Ctrl+Shift+M or map(hide) in the console turns it
 * off for the show, map(show) on again. Things whose line says map:[[...]]
 * keep their fit either way; the handles are only for fitting.
 *
 *   setMapped(id, { points, onMove, closed })   a thing with map:, its points now
 *   dropMapped(id)                              flat again (map:0), or gone
 *   setHandlesShown(on) / handlesShown()        mapping on or off, everywhere
 *   mapHtml(id, v, opts) / rebend(id)           the same for a thing drawn in HTML
 *                                               (a picture, the camera, a box), bent by CSS
 */

import { stageBand } from "./viewBand.js";
import { cssWarpFrom, mapPoints, mapText } from "./mapWarp.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const items = new Map();          // id -> { points, onMove, closed }
let shown = false;
let layer = null, outlines = null;

/** 0..1 of the stage at a point on the screen, and back. */
function fractionAt(x, y) {
  const b = stageBand();
  return [(x - b.left) / (b.width || 1), (y - b.top) / (b.height || 1)];
}
function screenAt([fx, fy]) {
  const b = stageBand();
  return [b.left + fx * b.width, b.top + fy * b.height];
}

/** The handles' layer, over everything (the code in perform mode too): a press on a handle is a drag. */
function ensureLayer() {
  if (layer?.isConnected) return layer;
  layer = document.createElement("div");
  layer.id = "oscilla-map-handles";
  layer.style.cssText = "position:fixed;inset:0;z-index:39000;pointer-events:none;";
  outlines = document.createElementNS(SVG_NS, "svg");
  outlines.style.cssText = "position:absolute;inset:0;width:100%;height:100%;overflow:visible;pointer-events:none;";
  layer.appendChild(outlines);
  document.body.appendChild(layer);
  return layer;
}

function middle(points) {
  const n = points.length || 1;
  return [points.reduce((s, p) => s + p[0], 0) / n, points.reduce((s, p) => s + p[1], 0) / n];
}

const HANDLE = "position:absolute;width:16px;height:16px;margin:-8px 0 0 -8px;border-radius:50%;"
  + "box-sizing:border-box;background:#fff;border:3px solid #000;box-shadow:0 0 0 1.5px #fff;"
  + "pointer-events:auto;cursor:move;touch-action:none;";

/** Draw one thing's handles and outline where its points are now. */
function draw(id) {
  const it = items.get(id);
  if (!it) return;
  if (!shown) { it.el?.remove(); it.line?.remove(); it.el = it.line = null; return; }
  ensureLayer();
  if (!it.el?.isConnected) {
    it.el = document.createElement("div");
    it.el.dataset.mapFor = id;
    layer.appendChild(it.el);
    it.line = document.createElementNS(SVG_NS, "polyline");
    it.line.setAttribute("fill", "none");
    it.line.setAttribute("stroke", "#fff");
    it.line.setAttribute("stroke-width", "1.5");
    it.line.setAttribute("stroke-dasharray", "6 4");
    it.line.style.filter = "drop-shadow(0 0 1px #000)";
    outlines.appendChild(it.line);
  }
  // one handle for each point, and the middle (a cross) moving them all
  const want = it.points.length + 1;
  while (it.el.children.length < want) {
    const h = document.createElement("div");
    h.style.cssText = HANDLE;
    h.addEventListener("pointerdown", (e) => grab(e, id, [...it.el.children].indexOf(h)));
    it.el.appendChild(h);
  }
  while (it.el.children.length > want) it.el.lastChild.remove();
  const hs = [...it.el.children];
  it.points.forEach((p, i) => {
    const [x, y] = screenAt(p);
    hs[i].style.left = `${x}px`; hs[i].style.top = `${y}px`;
    hs[i].title = `${id}: point ${i + 1} -- [${p.map(n => +n.toFixed(3)).join(", ")}]`;
  });
  const mid = hs[hs.length - 1];
  const [mx, my] = screenAt(middle(it.points));
  mid.style.left = `${mx}px`; mid.style.top = `${my}px`;
  mid.style.borderRadius = "2px";
  mid.style.transform = "rotate(45deg) scale(0.8)";
  mid.title = `${id}: move it all`;
  const pts = it.points.map(screenAt);
  if (it.closed !== false) pts.push(pts[0]);
  it.line.setAttribute("points", pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" "));
}

/**
 * A press on a handle: that point (or, the middle, all of them) follows the
 * pointer, the thing bent as it goes; let go, onMove(points, true) -- the
 * console writes the fit into the line.
 */
function grab(e, id, index) {
  const it = items.get(id);
  if (!it || e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const h = e.currentTarget;
  try { h.setPointerCapture(e.pointerId); } catch { /* a pointer the page did not see go down */ }
  const all = index >= it.points.length;
  const start = fractionAt(e.clientX, e.clientY);
  const from = it.points.map(p => [...p]);
  let moved = false;
  const move = (ev) => {
    const [x, y] = fractionAt(ev.clientX, ev.clientY);
    const dx = x - start[0], dy = y - start[1];
    it.points = all ? from.map(([px, py]) => [px + dx, py + dy]) : from.map((p, i) => (i === index ? [p[0] + dx, p[1] + dy] : p));
    moved = true;
    draw(id);
    it.onMove?.(it.points, false);
  };
  const up = () => {
    h.removeEventListener("pointermove", move);
    h.removeEventListener("pointerup", up);
    h.removeEventListener("pointercancel", up);
    if (moved) it.onMove?.(it.points, true);
  };
  h.addEventListener("pointermove", move);
  h.addEventListener("pointerup", up);
  h.addEventListener("pointercancel", up);
}

/** A thing with map:, its points now (fractions of the stage); its handles drawn if mapping is on. */
export function setMapped(id, { points, onMove, closed = true }) {
  const it = items.get(id) ?? {};
  Object.assign(it, { points: points.map(p => [...p]), onMove, closed });
  items.set(id, it);
  draw(id);
}

/** Flat again, or taken away: no handles. */
export function dropMapped(id) {
  const it = items.get(id);
  if (!it) return;
  it.el?.remove();
  it.line?.remove();
  items.delete(id);
}

export function isMapped(id) {
  return items.has(id);
}

/** Mapping on (the handles of everything with map: showing) or off. */
export function setHandlesShown(on) {
  shown = !!on;
  for (const id of items.keys()) draw(id);
}

export function handlesShown() {
  return shown;
}

// a resized window: the points are fractions of the stage, each thing bent to them anew
if (typeof window !== "undefined") {
  let t = null;
  window.addEventListener("resize", () => {
    clearTimeout(t);
    t = setTimeout(() => {
      for (const [id, it] of items) { it.onMove?.(it.points, false, { resized: true }); draw(id); }
    }, 150);
  });
}

// ---- things drawn in HTML: bent by a CSS matrix3d ------------------------------
//
// SVG takes no 3D transform (Chrome draws an SVG element's matrix3d flat),
// HTML does -- inside a foreignObject too. A picture, the camera's canvas, a
// film, a sound's box's canvases are bent where they are, as elements.

const html = new Map();           // id -> { opts, points }
const OFF = new Set(["0", "off", "false", "none"]);
const _sent = new Map();

/**
 * An element bent so that `part` of it -- [x, y, w, h] of its own px, all of
 * it by default -- lands on `points` (fractions of the stage). Measured
 * unbent: its own px to the screen, through whatever scales it (the score's
 * zoom, a foreignObject's frame); turned (a rotate()), it would come out
 * wrong. A foreignObject round it lets it out of its frame.
 */
function bendEl(el, points, part) {
  el.style.transform = "";
  const r = el.getBoundingClientRect();
  const w = el.offsetWidth || r.width, h = el.offsetHeight || r.height;
  if (!w || !h || !r.width || !r.height) return;
  const sx = r.width / w, sy = r.height / h;
  const quad = points.map(f => { const [x, y] = screenAt(f); return [(x - r.left) / sx, (y - r.top) / sy]; });
  el.style.transformOrigin = "0 0";
  el.style.transform = cssWarpFrom(part ?? [0, 0, w, h], quad);
  const fo = el.closest?.("foreignObject");
  if (fo) fo.style.overflow = "visible";
}

function unbendEl(el) {
  el.style.transform = "";
  el.style.transformOrigin = "";
}

function bendAll(id) {
  const m = html.get(id);
  if (!m) return;
  for (const t of m.opts.targets() || []) if (t?.el) bendEl(t.el, m.points, t.part);
}

/**
 * map: on a thing drawn in HTML.
 *   id         its handles' name: "cam:eye", "viz:s1"
 *   v          map:'s value: 1 (handles at its corners), [[x, y] ...] (the fit), 0 (flat)
 *   opts.flat()     its screen rect, unbent: where map:1's corners start
 *   opts.targets()  [{ el, part? }] the elements to bend, part: [x, y, w, h] of
 *                   the element's own px that lands on the points (a canvas
 *                   reaching past its box: the box's part of it)
 *   opts.line(pts)  the line that maps it, for the projector: viz(s1, map:[...])
 *   opts.write      { uid, cues } -- where the fit is written in, let go
 * A thing run again with its fit kept: rebend(id).
 */
export function mapHtml(id, v, opts) {
  if (OFF.has(String(v))) {
    const m = html.get(id);
    if (m) for (const t of m.opts.targets() || []) if (t?.el) unbendEl(t.el);
    html.delete(id);
    dropMapped(id);
    return false;
  }
  let points = mapPoints(v);
  if (!points || points.length !== 4) {
    const kept = html.get(id)?.points;
    if (kept) points = kept;
    else {
      // flat first, to read where its corners are
      for (const t of opts.targets() || []) if (t?.el) unbendEl(t.el);
      const r = opts.flat();
      if (!r?.width || !r?.height) return false;
      points = [[r.left, r.top], [r.right, r.top], [r.right, r.bottom], [r.left, r.bottom]].map(([x, y]) => fractionAt(x, y));
    }
    setHandlesShown(true);
  }
  html.set(id, { opts, points });
  bendAll(id);
  setMapped(id, {
    points,
    onMove: (now, last, how) => {
      const m = html.get(id);
      if (!m) return;
      m.points = now;
      bendAll(id);
      if (how?.resized) return;
      const t = performance.now();
      if (last || t - (_sent.get(id) ?? 0) >= 80) {
        _sent.set(id, t);
        window.dispatchEvent(new CustomEvent("oscilla:live-mirror", { detail: { expr: opts.line(now), key: `map:${id}` } }));
      }
      if (last && opts.write) window.dispatchEvent(new CustomEvent("oscilla:write-key", { detail: { ...opts.write, key: "map", value: mapText(now) } }));
    },
  });
  return true;
}

/** Drawn anew (a picture of a slideshow, a box resized): bent to its fit again, if it has one. */
export function rebend(id) {
  if (html.has(id)) bendAll(id);
}

/** Taken away: its handles go, and nothing is bent for it any more. */
export function forgetHtml(id) {
  html.delete(id);
  dropMapped(id);
}

/** A line saying only its name and map: -- a fit dragged on the laptop, run by the projector: nothing else to do. */
export function onlyMap(args) {
  return args.some(a => a.type === "map") && args.every(a => ["uid", "map", "_fromViz"].includes(a.type));
}

